import React, { useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import Nav from "./components/common/Nav";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import keycloak, { keycloakInitOptions } from "./Keycloak";
import { TimezoneProvider } from "./context/TimezoneContext";
import PrivateRoute from "./components/common/PrivateRoute";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import OrganizationPage from "./components/organizations/OrganizationPage";
import SourcesPage from "./components/sources/SourcesPage";
import ConnectionsPage from "./components/connections/ConnectionsPage";
import AccountPage from "./components/account/AccountPage";
import { ManageOrganizationDetails } from "./components/organizations/ManageOrganizationDetails";
import DestinationsPage from "./components/destinations/DestinationsPage"
import { ManageConnectionDetails } from "./components/connections/ManageConnectionDetails";
import { ManageAddConnection } from "./components/connections/ManageAddConnection";
import { useKeycloakToken, LoginModal } from './components/common/useKeycloak';
import { ManageDestinationDetails } from "./components/destinations/ManageDestinationDetails";
import { ManageAddConnectionStepOne } from "./components/connections/ManageAddConnectionStepOne";
import { ManageAddConnectionStepTwo } from "./components/connections/ManageAddConnectionStepTwo";
import { ManageAddConnectionStepThree } from "./components/connections/ManageAddConnectionStepThree";
import { ManageDestinationModal } from "./components/destinations/ManageDestinationModal";
import { HelpProvider } from "./context/HelpContext";
import HelpSidePanel from "./components/common/HelpSidePanel";
import { trackPageView, setAnalyticsUserProperties, getPageTitle } from "./analytics/analytics";

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname, getPageTitle(location.pathname));
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <div className="min-h-screen bg-very-light-gray">
      <ReactKeycloakProvider authClient={keycloak} initOptions={keycloakInitOptions}>
        <TimezoneProvider>
          <HelpProvider>
            <BrowserRouter>
              <PageViewTracker />
              <Nav />
              <HelpSidePanel />
              <div className="px-8 desktop:px-[60px] py-6 pt-[115px]">
                <Routes>
                  <Route
                    path="/"
                    element={
                      <PrivateRoute>
                        <ConnectionsPage />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/account"
                    element={
                      <PrivateRoute>
                        <AccountPage />
                      </PrivateRoute>
                    } />
                  
                  {/* workspaces */}
                  <Route
                    path="/workspaces"
                    element={
                      <PrivateRoute>
                        <OrganizationPage />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/workspaces/:org_id"
                    element={
                      <PrivateRoute>
                        <ManageOrganizationDetails />
                      </PrivateRoute>
                    } />
                  
                  {/* sources */}
                  <Route
                    path="/sources" // sources (list)
                    element={
                      <PrivateRoute>
                        <SourcesPage />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/sources/destination/:dest_id"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />
                  <Route
                    path="/sources/destination/:dest_id/configuration"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />
                  
                  {/* Destinations */}
                   <Route
                    path="/destinations"
                    element={
                      <PrivateRoute>
                        <DestinationsPage />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/destinations/new"
                    element={
                      <PrivateRoute>
                        <ManageDestinationModal onToggleModal={() => window.history.back()} />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/destinations/:dest_id"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />
                  <Route
                    path="/destinations/:dest_id/configuration"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />
                  <Route
                    path="/destination/:dest_id"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />
                  <Route
                    path="/destination/:dest_id/configuration"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />

                  {/* Connections */}
                  <Route
                    path="/connections/:connection_id"
                    element={
                      <PrivateRoute>
                        <ManageConnectionDetails />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/:connection_id/:subpage"
                    element={
                      <PrivateRoute>
                        <ManageConnectionDetails />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/:connection_id/:subpage/newdestination"
                    element={
                      <PrivateRoute>
                        <ManageDestinationModal onToggleModal={() => window.history.back()} />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/:connection_id/:subpage/:dest_id"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/:connection_id/:subpage/:dest_id/configuration"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />

                  {/* Add connection flow  */}
                  <Route
                    path="/connections/new"
                    element={
                      <PrivateRoute>
                        <ManageAddConnectionStepOne />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/new/configureprovider/:pt_value"
                    element={
                      <PrivateRoute>
                        <ManageAddConnectionStepTwo />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/new/:connection_id/configuredestinations"
                    element={
                      <PrivateRoute>
                        <ManageAddConnectionStepThree />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/new/:connection_id/configuredestinations/newdestination"
                    element={
                      <PrivateRoute>
                        <ManageDestinationModal onToggleModal={() => window.history.back()} />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/new/:connection_id/configuredestinations/destination/:dest_id"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/new/:connection_id/configuredestinations/destination/:dest_id/configuration"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />
                  
                </Routes>
                <ToastContainer />
                <Content />
              </div>
              </BrowserRouter>
          </HelpProvider>
        </TimezoneProvider>
      </ReactKeycloakProvider>
    </div>
  );
}

function Content() {
  const [token, showLoginModal, handleLogin, isSessionExpired] = useKeycloakToken();

  useEffect(() => {
    if (token && typeof token === 'string') {
      setAnalyticsUserProperties(token);
    }
  }, [token]);

  return (
    <>
      {(showLoginModal === true) && <LoginModal showLoginModal={showLoginModal} handleLogin={handleLogin} isSessionExpired={isSessionExpired} />}
    </>
  );
}

export default App;
